import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useInviteUser } from '../../hooks/useUserQueries';
import { ManagedUser } from '../../types/auth.types';
import { LedgerDialog, LedgerField, LedgerInput } from '../ui';

const schema = yup.object({
  email: yup.string().default('').required('Email is required').email('Enter a valid email'),
  firstName: yup
    .string()
    .default('')
    .required('First name is required')
    .test('non-blank', 'First name is required', (value) => Boolean(value && value.trim()))
    .max(150, 'First name must be 150 characters or fewer'),
  lastName: yup
    .string()
    .default('')
    .required('Last name is required')
    .test('non-blank', 'Last name is required', (value) => Boolean(value && value.trim()))
    .max(150, 'Last name must be 150 characters or fewer'),
});

type InviteUserFormValues = yup.InferType<typeof schema>;

const defaultValues: InviteUserFormValues = {
  email: '',
  firstName: '',
  lastName: '',
};

interface InviteUserDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * Receives the created user (already through the house transform). A fresh
   * invitee holds no role, and role-less users are invisible to staff admins'
   * scoped lists — so the caller needs the record to chain into granting the
   * first role.
   */
  onSuccess: (created: ManagedUser) => void;
}

export const InviteUserDialog: React.FC<InviteUserDialogProps> = ({ open, onClose, onSuccess }) => {
  const [error, setError] = useState<string | null>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);

  const inviteUser = useInviteUser();
  const saving = inviteUser.isPending;

  const {
    register,
    handleSubmit,
    reset,
    setError: setFieldError,
    formState: { errors, isSubmitting },
  } = useForm<InviteUserFormValues>({ resolver: yupResolver(schema), defaultValues, mode: 'onTouched' });

  // Reseed whenever the dialog opens so a stale error strip never leaks into
  // the next open.
  useEffect(() => {
    setError(null);
    reset(defaultValues);
  }, [open, reset]);

  const handleClose = () => {
    reset(defaultValues);
    setError(null);
    onClose();
  };

  const onSubmit = async (values: InviteUserFormValues) => {
    setError(null);
    try {
      // Re-inviting a never-activated user is a 200 on the server — it just
      // re-sends the invitation, so this path succeeds like a fresh invite.
      const created = await inviteUser.mutateAsync({
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
      });
      onSuccess(created);
      handleClose();
    } catch (err) {
      const data = (err as { response?: { data?: { email?: string[]; detail?: string } } }).response
        ?.data;
      if (data?.email?.length) {
        setFieldError('email', { message: data.email[0] });
      } else {
        setError(data?.detail || 'Failed to send invitation');
      }
    }
  };

  const emailField = register('email');

  return (
    <LedgerDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
      title="Invite User"
      size="md"
      preventClose={saving}
      initialFocus={emailInputRef}
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            disabled={saving}
            className="cursor-pointer border border-ink px-3 py-1.5 text-scale-1 text-ink hover:bg-paper-raised disabled:cursor-default disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="invite-user-form"
            disabled={saving || isSubmitting}
            className="cursor-pointer bg-accent px-3 py-1.5 text-scale-1 font-semibold text-paper hover:bg-accent-hover disabled:cursor-default disabled:opacity-60"
          >
            {saving ? 'Sending…' : 'Send Invitation'}
          </button>
        </>
      }
    >
      <form id="invite-user-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        {error && (
          <p role="alert" className="mb-3 border border-sev-critical/40 px-3 py-2 text-scale-1 text-sev-critical">
            {error}
          </p>
        )}

        <LedgerField label="Email" htmlFor="invite-email" error={errors.email?.message}>
          <LedgerInput
            id="invite-email"
            type="email"
            required
            invalid={!!errors.email}
            {...emailField}
            ref={(element) => {
              emailField.ref(element);
              emailInputRef.current = element;
            }}
          />
        </LedgerField>

        <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
          <LedgerField label="First Name" htmlFor="invite-first-name" error={errors.firstName?.message}>
            <LedgerInput
              id="invite-first-name"
              required
              invalid={!!errors.firstName}
              {...register('firstName')}
            />
          </LedgerField>

          <LedgerField label="Last Name" htmlFor="invite-last-name" error={errors.lastName?.message}>
            <LedgerInput
              id="invite-last-name"
              required
              invalid={!!errors.lastName}
              {...register('lastName')}
            />
          </LedgerField>
        </div>
      </form>
    </LedgerDialog>
  );
};
